import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { EnhancedTableData, EnhancedTableHeaderData, EnhancedTableSort } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginInstrument as MarginInstrumentType } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';

import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { MarginInstrumentsTableData } from './MarginInstrumentTable.types';
import { MarginInstrument, MarginInstrumentDetailsText } from '../../../General';

type MarginPositionsHeaderData = { [key in keyof MarginInstrumentsTableData]: EnhancedTableHeaderData };

type Props = {
  search?: string;
  sortKey?: keyof MarginInstrumentsTableData;
};

const useMarginInstrumentsTable = ({ search, sortKey = 'instrument' }: Props) => {
  const [sort, setSort] = useState<EnhancedTableSort<MarginInstrumentsTableData>>({
    sortKey,
    sortDirection: 'DESC',
  });

  const { markets, isLoading, getInstrumentSymbol } = useMarginInstruments();
  const { getAssetByUuid } = useMarkets();
  const { getMarketPriceByInstrument } = useMarginPrices();
  const isDesktopView = useTailwindBreakpoint('md', true);
  const { t } = useTranslation('margin');

  const headers: MarginPositionsHeaderData = useMemo(
    () => ({
      instrument: {
        title: t('instruments.table.headers.instrument'),
        alignment: 'start',
        sortable: true,
        enabled: isDesktopView,
      },
      bid: {
        title: t('instruments.table.headers.bid'),
        alignment: 'end',
        sortable: true,
        className: 'min-w-[120px]',
        enabled: isDesktopView,
      },

      ask: {
        title: t('instruments.table.headers.ask'),
        alignment: 'end',
        sortable: true,
        className: 'min-w-[120px]',
        enabled: isDesktopView,
      },

      dailyChange: {
        title: t('instruments.table.headers.dailyChange'),
        alignment: 'end',
        sortable: true,
        className: 'min-w-[120px]',
        enabled: isDesktopView,
      },

      instrumentXs: {
        title: t('instruments.table.headers.instrument'),
        alignment: 'start',
        sortable: false,
        enabled: !isDesktopView,
      },
      priceChange: {
        title: t('instruments.table.headers.dailyChange'),
        alignment: 'end',
        sortable: false,
        enabled: !isDesktopView,
      },
    }),
    [isDesktopView, t],
  );

  const filteredMarkets = useMemo(() => {
    if (!markets) return [];

    return Object.values(markets).filter((m) => {
      if (!m.long && !m.short && !m.open && !m.close) return false;
      if (!search) return true;

      const searchLowered = search.toLocaleLowerCase();
      const primary = getAssetByUuid(m.primaryAssetUuid);
      const secondary = getAssetByUuid(m.secondaryAssetUuid);
      const market = `${secondary?.code}${primary?.code}`;

      if (
        secondary?.name.toLocaleLowerCase().includes(searchLowered) ||
        secondary?.code.toLocaleLowerCase().includes(searchLowered) ||
        market.toLocaleLowerCase().includes(searchLowered)
      ) {
        return true;
      }

      return false;
    });
  }, [getAssetByUuid, markets, search]);

  const data: EnhancedTableData<MarginInstrumentsTableData, MarginInstrumentType>[] = useMemo(
    () =>
      filteredMarkets
        .map((market: MarginInstrumentType) => {
          const price = getMarketPriceByInstrument(market);
          const primary = getAssetByUuid(market.primaryAssetUuid);
          const secondary = getAssetByUuid(market.secondaryAssetUuid);

          return {
            value: market,

            instrument: {
              value: secondary?.rank || '',
              element: <MarginInstrument market={market} size='medium' showMarketName />,
            },
            bid: {
              value: Number(price?.bid),
              element: (
                <FlexLayout justifyContent='end'>
                  <MarginInstrumentDetailsText
                    market={market}
                    variant='bid'
                    size='medium'
                    weight='emphasis'
                    color='primary'
                  />
                </FlexLayout>
              ),
            },

            ask: {
              value: Number(price?.ask),
              element: (
                <FlexLayout justifyContent='end'>
                  <MarginInstrumentDetailsText
                    market={market}
                    variant='ask'
                    size='medium'
                    weight='emphasis'
                    color='primary'
                  />
                </FlexLayout>
              ),
            },

            dailyChange: {
              value: Number(price?.bidChangeYesterday),
              element: (
                <FlexLayout justifyContent='end'>
                  <MarginInstrumentDetailsText market={market} variant='dailyChange' size='medium' weight='bold' />
                </FlexLayout>
              ),
            },

            instrumentXs: {
              value: secondary?.rank || '',
              element: (
                <FlexLayout alignItems='center' spacing={16}>
                  <MarginInstrument market={market} size='medium' showMarketSymbol={false} />
                  <FlexLayout direction='column'>
                    <Body size='medium' weight='bold'>
                      {getInstrumentSymbol(market)}
                    </Body>
                    {secondary && primary && (
                      <Body size='small' color='secondary'>
                        {secondary.name} / {primary.name}
                      </Body>
                    )}
                  </FlexLayout>
                </FlexLayout>
              ),
            },

            priceChange: {
              value: Number(price?.bidChangeYesterday),
              element: (
                <FlexLayout direction='column' alignItems='end'>
                  <MarginInstrumentDetailsText
                    market={market}
                    variant='bid'
                    size='medium'
                    weight='bold'
                    color='primary'
                  />
                  <MarginInstrumentDetailsText market={market} variant='dailyChange' size='medium' weight='bold' />
                </FlexLayout>
              ),
            },
          };
        })
        .sort((a, b) => {
          const aValNum = a[sort.sortKey].value as number;
          const bValNum = b[sort.sortKey].value as number;

          switch (sort.sortKey) {
            case 'instrument':
              if (sort.sortDirection === 'ASC') return Big(bValNum).cmp(aValNum);
              return Big(aValNum).cmp(bValNum);
            case 'bid':
            case 'ask':
            case 'dailyChange':
              if (sort.sortDirection === 'ASC') return Big(aValNum).cmp(bValNum);
              return Big(bValNum).cmp(aValNum);

            default:
              return 1;
          }
        }),
    [
      filteredMarkets,
      getAssetByUuid,
      getInstrumentSymbol,
      getMarketPriceByInstrument,
      sort.sortDirection,
      sort.sortKey,
    ],
  );

  const onSort = useCallback(
    (newSort?: EnhancedTableSort<MarginInstrumentsTableData>) => {
      const tableData: EnhancedTableData<MarginInstrumentsTableData, MarginInstrumentType>[] = Object.assign([], data);

      if (!newSort) return tableData;

      setSort(newSort);
    },
    [data],
  );

  return {
    initialSort: sort,
    headers,
    data,
    isLoading,
    onSort,
  };
};

export { useMarginInstrumentsTable };
